/* eslint-disable max-classes-per-file */

import { Card, ControlGroup, FormGroup, HTMLSelect, InputGroup, Switch } from '@blueprintjs/core'
import { DateInput } from '@blueprintjs/datetime'
import React from 'react'
import Select, { Creatable } from 'react-select'
import { formatDate as momentFormatDate, DATE_AT_TIME } from '../../../../utils/dateHelpers'
import Client from '../../../../client'
import { LoadableSelect, rightElementCurrencyTag } from '../../../../components/Inputs'
import NullSafeNumericInput from '../../../../components/NullSafeNumericInput'
import ListingTypes from '../../../../constants/ebay/ListingTypes'
import { sortAlpha } from '../../../../utils'
import { InputLabel } from '../../../../components/inputs/InputLabel'
import { renderError, stylizeTitle } from '../../../products_v5/components/JoinedForm/Helpers'
import { ConditionDescriptionDisabledHelp } from '../../../products_v5/components/JoinedForm/EbayCard/Inputs'

export function StartingPrice(props) {
  return (
    <FormGroup label={stylizeTitle('Starting Price', props.error)} helperText={renderError(props.error)}>
      <NullSafeNumericInput
        min={1}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        minorStepSize={0.01}
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        fill
        value={props.value}
      />
    </FormGroup>
  )
}

export function BestOfferAutoAcceptPrice(props) {
  return (
    <FormGroup label="Auto accept if greater than or equal to">
      <NullSafeNumericInput
        disabled={props.disabled}
        min={1}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        minorStepSize={0.01}
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        fill
      />
    </FormGroup>
  )
}

export function MinimumBestOfferPrice(props) {
  return (
    <FormGroup label="Auto decline if less than">
      <NullSafeNumericInput
        disabled={props.disabled}
        min={1}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        minorStepSize={0.01}
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        fill
        value={props.value}
      />
    </FormGroup>
  )
}

export function BidIncrement(props) {
  return (
    <FormGroup label="Bid Increment">
      <NullSafeNumericInput
        min={1}
        minorStepSize={0.01}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        fill
      />
    </FormGroup>
  )
}
export function ReservePrice(props) {
  return (
    <FormGroup label="Reserve Price">
      <NullSafeNumericInput
        min={0}
        clampValueOnBlur
        selectAllOnFocus
        minorStepSize={0.01}
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        fill
      />
    </FormGroup>
  )
}

export function BuyItNowPrice({ value, ...props }) {
  return (
    <FormGroup label="Buy It Now Price">
      <NullSafeNumericInput
        min={0}
        placeholder={props.placeholder}
        clampValueOnBlur
        selectAllOnFocus
        minorStepSize={0.01}
        onValueChange={(_, s) => props.onChange(s)}
        value={value}
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
        fill
        {...props}
      />
    </FormGroup>
  )
}

export function HandlingPrice(props) {
  return (
    <FormGroup label="Handling Price">
      <NullSafeNumericInput
        min={0}
        minorStepSize={0.01}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        leftIcon="dollar"
        rightElement={rightElementCurrencyTag(props.currencyCode)}
      />
    </FormGroup>
  )
}

interface EbayProductListingDetailProps {
  label: string
  value: string
  detail: string
  onChange: (value) => void
}

export const ProductListingDetail = React.memo(({ label, value, detail, onChange }: EbayProductListingDetailProps) => {
  if (!detail) return null

  return (
    <FormGroup label={label} labelInfo="optional">
      <InputGroup onChange={e => onChange(e.target.value)} value={value} placeholder="Does Not Apply" />
    </FormGroup>
  )
})

export function ListingDuration(props) {
  const options = [
    { name: '1 Day', value: 'Days_1' },
    { name: '3 Days', value: 'Days_3' },
    { name: '5 Days', value: 'Days_5' },
    { name: '7 Days', value: 'Days_7' },
    { name: '10 Days', value: 'Days_10' },
    { name: '30 Days', value: 'Days_30' },
    { name: "Good Til' Canceled", value: 'GTC' }
  ]
  if (props.includeBlank) {
    options.unshift({ name: 'None', value: '' })
  }

  return (
    <label className="bp5-label">
      Listing Duration
      <div>
        <Select
          value={props.value}
          onChange={v => props.onChange(v)}
          labelKey="name"
          valueKey="value"
          clearable={props.clearable || false}
          placeholder={props.placeholder || 'Select...'}
          simpleValue
          disabled={props.disabled}
          options={options}
          fill
        />
      </div>
    </label>
  )
}

export function ListingType(props) {
  const options = Object.entries(ListingTypes).reduce(
    (acc, [id, info]) => [...acc, { label: info.humanReadableName, value: id }],
    []
  )
  if (props.includeBlank) {
    options.unshift({ label: 'None', value: '' })
  }
  return (
    <label className="bp5-label">
      Listing Type
      <div>
        <Select
          value={props.value}
          onChange={v => props.onChange(v)}
          clearable={props.clearable || false}
          placeholder={props.placeholder || 'Select...'}
          disabled={props.disabled}
          simpleValue
          options={options}
          fill
        />
      </div>
    </label>
  )
}

export const RelatedProducts = props => (
  <div className="ebay-product" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridGap: '30px' }}>
    {(props.products || []).map(p => (
      <Card
        onClick={() => props.onChange(p.epid)}
        role="button"
        tabIndex={0}
        key={p.epid}
        elevation={1}
        interactive
        style={{
          border: p.epid === props.value ? '1px solid #106BA3' : '1px solid transparent'
        }}
      >
        <div>
          <h6>{p.name}</h6>
          <img alt="" src={p.imageUrl} style={{ maxHeight: '130px', display: 'block', margin: 'auto' }} />
        </div>
      </Card>
    ))}
  </div>
)

export const ConditionPicker = props => (
  <label className="bp5-label">
    Condition <span className="bp5-text-muted">Required</span>
    <Select
      value={props.value}
      onChange={v => props.onChange(v)}
      clearable={false}
      simpleValue
      options={sortAlpha(
        props.options.map(c => ({ value: c.value, label: c.displayName })),
        'label'
      )}
      className="react-select-same-height-as-blueprint"
    />
  </label>
)

interface ConditionDescriptionProps {
  onChange: Function
  condition: number
  value?: any
}

export const ConditionDescription: React.FC<ConditionDescriptionProps> = React.memo(
  ({ onChange, value, condition }) => {
    // eBay ignores the condition description field if the item condition is new
    // https://developer.ebay.com/devzone/xml/docs/reference/ebay/additem.html#Request.Item.ConditionDescription
    const conditionIsNew = condition >= 1000 && condition <= 1499

    return (
      <FormGroup
        className="exclude-helper-text-from-height-calculation"
        label="Condition Description"
        helperText="Additional description for used items"
        disabled={conditionIsNew}
        labelInfo={conditionIsNew && <ConditionDescriptionDisabledHelp />}
      >
        <InputGroup
          value={value || ''}
          onChange={e => onChange('conditionDescription', e?.currentTarget?.value)}
          disabled={conditionIsNew}
        />
      </FormGroup>
    )
  }
)

export const StoreCategoryPicker = props => (
  <div style={{ maxWidth: 350 }}>
    <LoadableSelect
      title="eBay Store Category"
      load={() => Client.getEbayStores(props.marketAccountId)}
      optional
      process={d =>
        d.storeCategories.map(s => ({
          value: s.externalId,
          label: s.fullName
        }))
      }
      onChange={v => props.onChange(v)}
      value={props.value}
      autoload={props.value}
    />
  </div>
)

export const SpecificRenderer = props => {
  const options = props.options || []

  return options.map((s, i) => {
    // skip for required toggling
    if (s.required !== props.required) return null

    let Comp

    const value = props.specifics[s.name] || ''
    const recommendations = s.valueRecommendations.map(vr => ({ label: vr.value, value: vr.value }))
    const rules = s.validationRules

    if (rules.selectionMode === 'FreeText') {
      Comp = Creatable
      if (value && !recommendations.find(r => r.value === value)) {
        recommendations.push({ label: value, value })
      }
    } else if (rules.selectionMode === 'SelectionOnly') {
      Comp = Select
    }
    let shortCut = null

    if (rules.selectionMode === 'FreeText') {
      if (s.name === 'MPN') {
        shortCut = 'Does Not Apply'
      } else if (s.name === 'Brand') {
        shortCut = 'Unbranded'
      }
    }
    return (
      <label className="bp5-label" key={s.name}>
        <div style={{ display: 'flex' }}>
          {s.name}{' '}
          {s.required && (
            <span className="bp5-text-muted" style={{ marginLeft: 5 }}>
              Required
            </span>
          )}{' '}
          <a style={{ marginLeft: 'auto' }} onClick={() => props.onChange(i, shortCut)} role="button" tabIndex={0}>
            {shortCut}
          </a>{' '}
        </div>
        <Comp
          placeholder={rules.selectionMode === 'FreeText' ? 'Type or Select...' : 'Select...'}
          value={value}
          onChange={v => props.onChange(i, v)}
          options={recommendations}
          simpleValue
        />
      </label>
    )
  })
}

interface SellerProfilesState {
  options: any[] // TODO: this may be an array of seller profiles from Client.getEbaySellerProfilesV5()
  loading: boolean
}

interface SellerProfilesProps {
  marketAccountId: number
  style?: any
  shippingValue: any
  returnValue: any
  paymentValue: any
  onShippingChange: any
  onReturnChange: any
  onPaymentChange: any
}

export class SellerProfiles extends React.Component<SellerProfilesProps, SellerProfilesState> {
  constructor(props) {
    super(props)
    this.state = {
      options: [],
      loading: false
    }
  }

  componentDidMount() {
    this.getData()
  }

  getData = async () => {
    this.setState({ loading: true })
    try {
      const res = await Client.getEbaySellerProfilesV5(this.props.marketAccountId)
      this.setState({
        options: res.data.profiles,
        loading: false
      })
    } catch (_error) {
      this.setState({
        options: [],
        loading: false
      })
    }
  }

  getOptions = key => {
    return this.state.options
      .filter(p => p.hidden === false && p.profileType === key)
      .map(v => ({ value: v.externalId, label: v.name }))
  }

  render() {
    return (
      <div style={{ margin: '20px auto', display: 'grid', gridGap: '20px', gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div>
          <InputLabel title="Shipping Profile" style={this.props.style}>
            <Select
              {...this.props}
              placeholder="Select"
              options={this.getOptions('SHIPPING')}
              noResultsText={this.state.loading ? 'Loading...' : 'No results'}
              value={this.props.shippingValue}
              simpleValue
              onChange={v => this.props.onShippingChange(v)}
            />
          </InputLabel>
        </div>
        <div>
          <InputLabel title="Returns Profile" style={this.props.style}>
            <Select
              {...this.props}
              placeholder="Select"
              options={this.getOptions('RETURN_POLICY')}
              noResultsText={this.state.loading ? 'Loading...' : 'No results'}
              value={this.props.returnValue}
              simpleValue
              onChange={v => this.props.onReturnChange(v)}
            />
          </InputLabel>
        </div>
        <div>
          <InputLabel title="Payment Profile" style={this.props.style}>
            <Select
              {...this.props}
              placeholder="Select"
              options={this.getOptions('PAYMENT')}
              noResultsText={this.state.loading ? 'Loading...' : 'No results'}
              value={this.props.paymentValue}
              simpleValue
              onChange={v => this.props.onPaymentChange(v)}
            />
          </InputLabel>
        </div>
      </div>
    )
  }
}

interface ScheduleTimePickerState {
  select: string
}

interface ScheduleTimePickerProps {
  // eslint-disable-next-line react/no-unused-prop-types
  label?: string
  value: Date
  onChange: (date) => void
  disabled?: boolean
}

export class ScheduleTimePicker extends React.Component<ScheduleTimePickerProps, ScheduleTimePickerState> {
  constructor(props) {
    super(props)
    this.state = {
      select: props.value ? 'Scheduled' : 'Immediately'
    }
  }

  render() {
    const maxDate = new Date()
    maxDate.setDate(maxDate.getDate() + 20)

    return (
      <FormGroup label="Start Time">
        <ControlGroup fill vertical={false}>
          <div style={{ maxWidth: 115 }}>
            <HTMLSelect
              onChange={e => {
                this.setState({ select: e.target.value })
                if (e.target.value === 'Immediately') {
                  this.props.onChange(null)
                }
              }}
              disabled={this.props.disabled || false}
              options={['Immediately', 'Scheduled'].map(o => ({ label: o, value: o }))}
              value={this.state.select}
            />
          </div>
          <DateInput
            formatDate={date => momentFormatDate(date, DATE_AT_TIME)}
            parseDate={str => new Date(str)}
            value={this.props.value ? this.props.value.toISOString() : null}
            onChange={dateStr => this.props.onChange(dateStr ? new Date(dateStr) : null)}
            timePrecision="minute"
            disabled={this.props.disabled || this.state.select === 'Immediately'}
            maxDate={this.props.disabled ? undefined : maxDate}
            placeholder={this.state.select === 'Immediately' ? 'List Immediately' : 'Schedule Listing'}
            closeOnSelection={false}
          />
        </ControlGroup>
      </FormGroup>
    )
  }
}

interface UseTaxTableProps {
  checked: boolean
  onChange: (v) => void
}

export const UseTaxTable = React.memo(({ checked = false, onChange }: UseTaxTableProps) => (
  <Switch checked={checked} label="Use eBay tax table" onChange={() => onChange(!checked)} inline />
))

/* eslint-enable max-classes-per-file */
